// APP VIEWS in an AI answer — the widget contract, generalised (Nika
// 2026-10-01: "chatbot should be using all powerful functionalities from all
// apps ... also calendar, tickets - reusing ui and functionalities right in the
// chatbot").
//
// A VIEW is a read-only piece of an app's own UI the assistant may mount in its
// thread (main + sidebar), with params the model sets. Two declarers, ONE shape:
//
//   an installed app — manifest v2 `views[]` (beside `widgets[]`)
//   a built-in package (calendar, tickets — no manifest; lazy-folded into the
//     shell) — a `<pkg>AiViews()` provider in its EAGER function slice that
//     returns `{ app, fold, views }`
//
//   {
//     id: 'calendar-agenda',          /^[a-z0-9-]+$/, unique across apps
//     title, describe, icon, hue,     as a widget (describe = the ONE line the
//                                     model matches a question against)
//     component: 'CalendarAgendaEmbed' the key the app ships; mounted with
//                                     scope { params, widgetId, size, embedded }
//     params: { … }                   the widget param schema (enum / string /
//                                     number / boolean / range / date,
//                                     values / valuesFrom / default)
//     size, minSize                   s|m|l|xl — a view defaults to l
//     link: { route, query }          "Open in split": query values are a
//                                     param NAME (a range param gives its first
//                                     day) or `{ value }` (a literal)
//     link: { route, call }           a route the params alone cannot name
//                                     (the site's owner, its environment):
//                                     `el.call(call, params, root)` answers it
//                                     (a string starting with '/'), null hides
//                                     the Open
//     access: { admin?, permission?, collections?, route? }
//                                     who may see it — every condition must
//                                     hold (U5); the same checks the app pages
//                                     use: currentUserIsAdmin, hasPermission,
//                                     the workspace's records grant, and the
//                                     navigation gate of the app's page
//                                     (`route`: the module's feature flag and
//                                     the team app-access matrix,
//                                     navItemVisible — a member who cannot
//                                     open /mail sees no mail view, and the
//                                     catalog never offers it)
//   }
//
// EMPTY — the COMPONENT declares `empty` once on its root, the home slot
// grid's contract (components/HomeWidgetBody.js): `true` when its data has
// answered and the view shows only its one sentence (nothing matches, no such
// item, a failed read), `false` when it shows something, `null` while it is
// still reading. A Fly page reads it (functions/assistantBoard.js
// assistantBoardCardEmpty) and gives an empty section ONE row, never one line
// over a blank square:
//
//   TicketsAnswerView: {
//     state: { tickets: null },
//     empty: (el, s) => (Array.isArray(s.tickets) ? s.tickets.length === 0 : null),
//     …
//   }
//
// A view that does not declare it keeps its full size.
//
// ↗ OPEN — the COMPONENT may declare `openRoute` once on its root: the route
// of its own page WITH the filters the view resolved — a teammate's name to
// their email, a project's name to its id — which `link` cannot know before
// the view reads. A card's ↗ Open (a Fly section, assistantBoardCardOpen) and
// a thread block's Open in split (assistantBlockOpen) read it on the mounted
// body and fall back to `link`:
//
//   TicketsAnswerView: {
//     state: { route: null },
//     openRoute: (el, s) => s.route || null,
//     …
//   }
//
// RESOLVED PARAMS — the COMPONENT may declare `resolvedParams` once on its
// root: the params it SHOWS, in its own param names, once it has resolved them
// — a site's key when the model named no site, a teammate's email for a name
// (null while reading). A Fly page's next ask carries them as the sections on
// screen (functions/assistantFlyContext.js, the ask scope's `widgets`), so a
// follow-up stays on the subject the card showed:
//
//   SitePreviewView: {
//     state: { site: null },
//     resolvedParams: (el, s) => (s.site ? { project: s.site.key } : null),
//     …
//   }
//
// The board vocabulary (assistantBoard.js boardVocabularyFor) reads the views
// through assistantAppViewSources and keeps them beside the widgets, so the
// widget path — validation, the card, the mount, Open in split — serves them.
// A view the member may not see goes to `denied`: the card says one quiet line.
//
// Frank note: el.call serializes each export on its own (FA204) — every
// export here is self-contained.

// THE PAGE A VIEW IS GATED ON (`access.route`, assistantAppViewAllowed): its
// own declaration, else the page its link opens — a member who cannot open
// that page (the module's flag, the team app-access matrix) sees no view of it
// either, and the catalog never offers it (U5). A view that declares no route
// and links nowhere (or to the home) is gated by its other conditions only.
// '' when none.
export const assistantAppViewRoute = function assistantAppViewRoute(view) {
  const v = view && typeof view === 'object' ? view : null
  if (!v) return ''
  const own = v.access && typeof v.access === 'object' ? v.access.route : null
  if (typeof own === 'string' && own.startsWith('/')) return own
  const link = v.link && typeof v.link === 'object' ? v.link.route : null
  if (typeof link !== 'string' || !link.startsWith('/') || link.startsWith('//')) return ''
  const path = link.split('?')[0].split('#')[0]
  return path && path !== '/' ? path : ''
}

// Every source of views this workspace has: the takeover module and each
// installed app (`views[]` on its published manifest), then the built-in
// packages' providers. Each source: { app, views, fold }. A view without its
// own page gate carries the one its link names (assistantAppViewRoute), so
// every reader — the board vocabulary, the thread's blocks — gates the same.
export const assistantAppViewSources = function assistantAppViewSources(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || {}
  const out = []
  const gated = (views) =>
    views.map((v) => {
      if (!v || typeof v !== 'object') return v
      const has = v.access && typeof v.access === 'object' ? v.access.route : null
      if ((typeof has === 'string' && has) || !el || typeof el.call !== 'function') return v
      const route = el.call('assistantAppViewRoute', v)
      return route ? { ...v, access: { ...(v.access || {}), route } } : v
    })
  const wp = r.workspaceProject
  if (wp && Array.isArray(wp.views)) out.push({ app: wp, views: gated(wp.views), fold: null })
  for (const app of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) {
    if (app && Array.isArray(app.views)) out.push({ app, views: gated(app.views), fold: null })
  }
  // The built-in packages. A provider not registered (a host without the
  // package) adds nothing. files / preview / canvas: the workspace's files,
  // one of its websites live, its projects (Nika 2026-10-17: "fly should be
  // able to display all functionality … website preview, tasks, files").
  // workspace / meet / chat / documents / mail: the team and its agents, the
  // notifications, the reminders, the calls and a call's transcript, a chat
  // channel, a note, the mail (Nika 2026-10-18: "fly should cover all
  // functionality that would be possible otherwise").
  const PROVIDERS = [
    'calendarAiViews',
    'ticketsAiViews',
    'filesAiViews',
    'previewAiViews',
    'canvasAiViews',
    'workspaceAiViews',
    'meetAiViews',
    'chatAiViews',
    'documentsAiViews',
    'mailAiViews'
  ]
  const fns = (el && el.context && el.context.functions) || null
  for (const name of PROVIDERS) {
    if (fns && typeof fns[name] !== 'function') continue
    let got = null
    try {
      got = el.call(name)
    } catch (_) {
      got = null
    }
    if (!got || typeof got !== 'object' || !got.app || !Array.isArray(got.views)) continue
    out.push({
      app: got.app,
      views: gated(got.views),
      fold: typeof got.fold === 'string' ? got.fold : null
    })
  }
  return out
}

// May THIS member see a view? Every declared condition must hold. No `access`
// = every member who can read the answer.
export const assistantAppViewAllowed = function assistantAppViewAllowed(access, root) {
  const el = this
  const a = access && typeof access === 'object' ? access : null
  if (!a) return true
  const r = root || (el && el.state && el.state.root) || {}
  const ask = (name, ...args) => {
    try {
      return el.call(name, ...args)
    } catch (_) {
      return false
    }
  }
  if (a.admin === true && !ask('currentUserIsAdmin', r)) return false
  if (typeof a.permission === 'string' && a.permission && !ask('hasPermission', r, a.permission)) {
    return false
  }
  if (Array.isArray(a.collections) && a.collections.length) {
    // The records grant the server applies: each installed app's
    // settings.workspaceApps[].capabilities.records.collections (and the
    // takeover module's), unioned. No entry carries a grant → the shell cannot
    // tell; the view mounts and the plane decides (the app page's own rule).
    const s = (r.workspace && r.workspace.settings) || {}
    const entries = (Array.isArray(s.workspaceApps) ? s.workspaceApps : []).concat(
      s.workspaceModule ? [s.workspaceModule] : []
    )
    const granted = new Set()
    let known = false
    for (const e of entries) {
      const rec = e && e.capabilities && e.capabilities.records
      if (!rec || !Array.isArray(rec.collections)) continue
      known = true
      for (const c of rec.collections) {
        const key = typeof c === 'string' ? c : c && c.key
        if (key) granted.add(String(key))
      }
    }
    if (known && !a.collections.every((c) => granted.has(String(c)))) return false
  }
  // The app's page: its module flag and the team app-access matrix — the one
  // navigation gate (shared functions/featureFlags.js navItemVisible).
  if (typeof a.route === 'string' && a.route.startsWith('/')) {
    let open = true
    try {
      open = el.call('navItemVisible', r, a.route) !== false
    } catch (_) {
      open = true
    }
    if (!open) return false
  }
  return true
}

// "Open in split" for a view: its route + the query its params fill. A query
// value is a param NAME (a range param gives its first day), or `{ value }`.
// Empty params leave their key out. null without a route. A link that names a
// `call` is that function's answer for the params and the root (a site view's
// `/preview?owner&key&env` — functions/previewAnswer.js previewAnswerLink):
// only a same-shell path ('/…', never '//'), else null.
export const assistantAppViewLink = function assistantAppViewLink(link, params, root) {
  const l = link && typeof link === 'object' ? link : null
  if (!l || typeof l.route !== 'string' || !l.route.startsWith('/')) return null
  const p = params && typeof params === 'object' ? params : {}
  if (typeof l.call === 'string' && l.call) {
    const fns = this && this.context && this.context.functions
    if (!fns || typeof fns[l.call] !== 'function') return null
    let to = null
    try {
      to = this.call(l.call, p, root || (this.state && this.state.root) || {})
    } catch (_) {
      to = null
    }
    return typeof to === 'string' && to.startsWith('/') && !to.startsWith('//') ? to : null
  }
  const q = l.query && typeof l.query === 'object' ? l.query : {}
  const parts = []
  for (const key of Object.keys(q)) {
    const src = q[key]
    let v = null
    if (src && typeof src === 'object' && src.value != null) v = src.value
    else if (typeof src === 'string') v = p[src]
    if (v == null || v === '') continue
    let str = String(v)
    if (/^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/.test(str)) str = str.slice(0, 10)
    parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(str))
  }
  return parts.length ? l.route + (l.route.includes('?') ? '&' : '?') + parts.join('&') : l.route
}

// The route a mounted view's BODY declares (its `openRoute`, above). `host`
// is the element the body is mounted into — a card's CardBody, a thread
// block's BlockWidget. An app route ('/…', never another origin's '//…') or
// null.
export const assistantAppViewOwnRoute = function assistantAppViewOwnRoute(host) {
  const body = host && host.content
  const route = body ? body.openRoute : null
  return typeof route === 'string' && /^\/(?!\/)/.test(route) ? route : null
}

// Fold a built-in package's FULL context into the live registry before one of
// its views mounts (the route folds' add-missing merge — a later visit to the
// page merges nothing twice). Idempotent: the module system caches the chunk.
// true when registered, false when the chunk failed (the card keeps its
// skeleton; the thread never throws).
export const ensureAppViewContext = async function ensureAppViewContext(key) {
  // chat / mail / meet / documents fold through their OWN openers (functions/
  // chatFold.js, mailFold.js, meetFold.js, documentsFold.js): the same
  // add-missing merge plus each package's latch (and the chat / mail overlay
  // slot), so a view never opens a second import path into a package the
  // shell already folds.
  const OPENERS = {
    chat: 'ensureChatContext',
    mail: 'ensureMailContext',
    meet: 'ensureMeetContext',
    documents: 'ensureDocumentsContext'
  }
  if (Object.prototype.hasOwnProperty.call(OPENERS, key)) {
    try {
      return (await this.call(OPENERS[key])) === true
    } catch (err) {
      console.warn('[assistantAppViews] context fold failed:', key, err && err.message)
      return false
    }
  }
  const rootEl = typeof this.getRoot === 'function' ? this.getRoot() : this
  const ctx = rootEl && rootEl.context
  if (!ctx) return false
  let mod = null
  try {
    if (key === 'calendar') mod = await import('@symbo-ls/workspace-calendar/context.js')
    else if (key === 'tickets') mod = await import('@symbo-ls/workspace-tickets/context.js')
    // The files view rides the eager slice; the fold is the safety net for a
    // host that registered the provider without it.
    else if (key === 'files') mod = await import('@symbo-ls/workspace-files/context.js')
    // A site view's INSPECT: the preview app's render pipeline (renderProject
    // and the Scene it was built for) — the /preview route's own fold
    // (pages/index.js), folded on demand from a view that rides the eager
    // slice (@symbo-ls/workspace-preview functions/previewAnswer.js).
    else if (key === 'preview') mod = await import('@symbo-ls/workspace-preview/context.js')
    // The site builder's Publish: the project's existing publish dialog
    // (openPublishModal + ProjectPublishModal) — the canvas editor's own
    // context, the one the shell editor folds (pages/projectRedirect.js).
    else if (key === 'canvas') mod = await import('@symbo-ls/workspace-canvas/context.js')
    else return false
  } catch (err) {
    console.warn('[assistantAppViews] context fold failed:', key, err && err.message)
    return false
  }
  const lib = mod && mod.default ? mod.default : mod
  if (!lib || (!lib.components && !lib.functions)) return false
  const add = (from, to) => {
    if (!from || !to) return
    for (const k of Object.keys(from)) if (!to[k]) to[k] = from[k]
  }
  add(lib.components, ctx.components)
  add(lib.functions, ctx.functions)
  return true
}

// The bundles the catalog loads ITSELF, per `<lang>/<namespace>` — kept here,
// not in the i18n loader's cache (a language switch evicts that one). Loaded
// through the shell's own `loadI18nNamespace` (no network on a published
// record: it degrades to the base language). A turn sent before a bundle
// arrives carries the words already loaded; the next one carries all.
const CATALOG_BUNDLES = new Map()
const CATALOG_PENDING = new Set()
const KEY_RE = /^[a-z][A-Za-z0-9]*(\.[A-Za-z0-9_-]+)+$/

// Tests only: the bundle cache is module state.
export const _resetCatalogBundles = () => {
  CATALOG_BUNDLES.clear()
  CATALOG_PENDING.clear()
}

function catalogDictionaryKeys(entry) {
  const keys = new Set()
  for (const item of [
    ...(Array.isArray(entry.views) ? entry.views : []),
    ...(Array.isArray(entry.forms) ? entry.forms : [])
  ]) {
    if (!item || typeof item !== 'object') continue
    if (typeof item.title === 'string') keys.add(item.title)
    for (const bag of [item.params, item.fields]) {
      if (!bag || typeof bag !== 'object') continue
      for (const def of Object.values(bag)) {
        const labels = def && typeof def === 'object' ? def.labels : null
        if (!labels || typeof labels !== 'object') continue
        for (const label of Object.values(labels)) if (typeof label === 'string') keys.add(label)
      }
    }
  }
  return [...keys].filter((k) => KEY_RE.test(k))
}

function catalogLangs(tr) {
  const w = typeof window !== 'undefined' ? window : globalThis
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  const langs = new Set(Object.keys(tr || {}))
  for (const code of (reg && Array.isArray(reg.langs) && reg.langs) || []) langs.add(code)
  return [...langs]
}

function prefetchCatalogBundles(el, keys) {
  const w = typeof window !== 'undefined' ? window : globalThis
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  if (!reg || !Array.isArray(reg.langs) || !el || typeof el.call !== 'function') return
  const known = new Set(Array.isArray(reg.namespaces) ? reg.namespaces : [])
  const namespaces = [...new Set(keys.map((k) => k.split('.')[0]))].filter((ns) => known.has(ns))
  for (const code of reg.langs) {
    for (const ns of namespaces) {
      const id = code + '/' + ns
      if (CATALOG_BUNDLES.has(id) || CATALOG_PENDING.has(id)) continue
      CATALOG_PENDING.add(id)
      let p = null
      try {
        p = el.call('loadI18nNamespace', code, ns)
      } catch (_) {
        p = null
      }
      Promise.resolve(p)
        .then((bundle) => {
          // A language with no bundle degrades to the base one: never store
          // English as another language's words.
          const degraded = code !== reg.baseLang && reg.base && bundle === reg.base[ns]
          if (bundle && typeof bundle === 'object' && !degraded) CATALOG_BUNDLES.set(id, bundle)
        })
        .catch(() => {})
        .finally(() => CATALOG_PENDING.delete(id))
    }
  }
}

// THE APP CATALOG a chat turn sends (`context.appCatalog`, server
// lane/aib-tools-views): the built-in packages' declarations — the RAW outputs
// of their providers (calendarAiViews, calendarAiForms, ticketsAiViews,
// ticketsAiForms). The server accepts system/<pkg> ids only from the client
// and merges a package's views and forms. Installed apps are not here: the
// server reads their manifests itself. A provider not registered, one that
// throws or answers nothing is left out; null when none answers.
//
// A view whose app page this member cannot open (`access.route` — the
// module's flag, the team app-access matrix) is left out: the model never
// offers what the member could not open (the server reads only admin /
// permission / collections). A view with no `access.route` of its own is gated
// on the page its link opens (assistantAppViewRoute). ORDER is the server's
// keep order: it keeps the first 16 app ids and 48 views (server ai-boards/
// appCatalog.js pickClientAppCatalog — 8 and 24 before server d44312e33,
// 2026-10-19, which production does not run yet: there the last two, notes
// and mail, are cut), so the apps asked about most come first
// (__tests__/assistantAppCatalogComms.test.js measures the payload).
export const assistantAppCatalog = function assistantAppCatalog() {
  const el = this
  const fns = (el && el.context && el.context.functions) || {}
  const NAMES = [
    'calendarAiViews',
    'calendarAiForms',
    'ticketsAiViews',
    'ticketsAiForms',
    'filesAiViews',
    'previewAiViews',
    'canvasAiViews',
    'workspaceAiViews',
    'meetAiViews',
    'chatAiViews',
    'documentsAiViews',
    'mailAiViews'
  ]
  const root = (el && el.state && el.state.root) || null
  // Its own page gate, else the page its link opens (assistantAppViewRoute).
  const routeOpen = (view) => {
    const route = el.call('assistantAppViewRoute', view)
    if (typeof route !== 'string' || !route.startsWith('/')) return true
    try {
      return el.call('navItemVisible', root, route) !== false
    } catch (_) {
      return true
    }
  }
  const out = []
  for (const name of NAMES) {
    if (typeof fns[name] !== 'function') continue
    let got = null
    try {
      got = el.call(name)
    } catch (_) {
      got = null
    }
    const id = got && got.app && typeof got.app.id === 'string' ? got.app.id : ''
    if (!/^system\/[a-z0-9-]+$/.test(id)) continue
    // WORDS, NOT KEYS: the server reads a field's / param's `labels` as text
    // or { en, ka } and cannot read the shell's bundles, so a bundle-key label
    // leaves as the { en, ka } pair of the loaded bundles (a key no bundle
    // knows, a literal text and a pair pass through). A copy: the provider's
    // own object is never touched.
    let entry = got
    try {
      entry = JSON.parse(JSON.stringify(got))
    } catch (_) {
      entry = got
    }
    if (Array.isArray(entry.views)) {
      const open = entry.views.filter(routeOpen)
      if (open.length !== entry.views.length) entry = { ...entry, views: open }
      if (!open.length && !(Array.isArray(entry.forms) && entry.forms.length)) continue
    }
    // EVERY supported language, not only the UI's: an answer is written in
    // the language of the ASK (live f5276428b: a Georgian ask from an English
    // shell met en-only words). Words come from the loaded translations, then
    // from the bundles this catalog loads itself (catalogBundles below).
    const tr = (el && el.context && el.context.polyglot && el.context.polyglot.translations) || {}
    const keys = catalogDictionaryKeys(entry)
    prefetchCatalogBundles(el, keys)
    const langs = catalogLangs(tr)
    const wordsOf = (code, key) => {
      const own = tr[code] && tr[code][key]
      if (typeof own === 'string' && own.trim()) return own.trim()
      const ns = key.split('.')[0]
      const fetched = CATALOG_BUNDLES.get(code + '/' + ns)
      const text = fetched && fetched[key]
      return typeof text === 'string' && text.trim() ? text.trim() : ''
    }
    const resolve = (labels) => {
      if (!labels || typeof labels !== 'object') return
      for (const value of Object.keys(labels)) {
        const key = labels[value]
        if (typeof key !== 'string') continue
        const pair = {}
        for (const code of langs) {
          const text = wordsOf(code, key)
          if (text) pair[code] = text
        }
        if (pair.en) labels[value] = pair
      }
    }
    const bags = []
    for (const f of Array.isArray(entry.forms) ? entry.forms : []) bags.push(f && f.fields)
    for (const v of Array.isArray(entry.views) ? entry.views : []) bags.push(v && v.params)
    // AN ENUM TRAVELS AS THE SERVER TAKES IT: its first 30 values (the server
    // offers the model no more — aiBoardSchema ENUM_MAX_VALUES) and the
    // labels of those values only; `valuesLive` (the client's own rule for a
    // workspace's live list, assistantBoard.js validateBoardSpec) stays home.
    // A site view's websites enum is the workspace's whole site list, and
    // three views carry it: untrimmed, a big workspace pushed the catalog
    // over the server's byte cap, which drops the WHOLE catalog.
    const ENUM_MAX = 30
    for (const bag of bags) {
      if (!bag || typeof bag !== 'object') continue
      for (const name of Object.keys(bag)) {
        const def = bag[name]
        if (!def || typeof def !== 'object') continue
        if (def.valuesLive !== undefined) delete def.valuesLive
        if (def.type === 'enum' && Array.isArray(def.values) && def.values.length > ENUM_MAX) {
          def.values = def.values.slice(0, ENUM_MAX)
        }
        if (def.type === 'enum' && Array.isArray(def.values) && def.labels) {
          const kept = new Set(def.values.map(String))
          for (const v of Object.keys(def.labels)) if (!kept.has(v)) delete def.labels[v]
        }
        resolve(def.labels)
      }
    }
    // A view's / form's TITLE stays its key (the shell renders the card from
    // it); its words ride beside it as `lang: { <lang>: { <key>: words } }`,
    // the shape of an installed app's dictionary (server appCatalog
    // pickClientAppCatalog). Without them the server could not name a built-in
    // view, and the AI answer used the model's free title (live b8ef3cc45:
    // "Here is team agenda for next week.").
    const lang = {}
    for (const item of [
      ...(Array.isArray(entry.views) ? entry.views : []),
      ...(Array.isArray(entry.forms) ? entry.forms : [])
    ]) {
      const key = item && typeof item.title === 'string' ? item.title : ''
      if (!key) continue
      for (const code of langs) {
        const text = wordsOf(code, key)
        if (!text) continue
        if (!lang[code]) lang[code] = {}
        lang[code][key] = text
      }
    }
    if (Object.keys(lang).length) entry.lang = lang
    out.push(entry)
  }
  return out.length ? out : null
}
